import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import useCanUpdateTemplateDeployment from "shared/Templates/access/useCanUpdateTemplateDeployment";
import useCanDeleteDeployment from "shared/Templates/access/useCanDeleteDeployment";
import useCanUpdateTemplateDeploymentInputs from "shared/Templates/access/useCanUpdateTemplateDeploymentInputs";
import { BlueprintDeployment } from "types/generated";

import { TemplateContext } from "../../../Context";
import { showDeleteConfirmation } from "../../DeleteConfirmation";
import { showUpdateDrawer } from "../../UpdateDrawer";

const TemplateDeploymentsRowActionsCell = ({
  item: deployment,
  ...cellProps
}: TableSimpleCellProps<BlueprintDeployment>) => {
  const { template: templateData, isMarketplace } = useTypedContext(TemplateContext);
  const navigate = useNavigate();
  const location = useLocation();

  const canUpdateTemplateDeployment = useCanUpdateTemplateDeployment()(
    templateData.space.id,
    deployment.state
  );

  const canUpdateTemplateDeploymentInputs = useCanUpdateTemplateDeploymentInputs()(
    templateData.space.id,
    deployment.state
  );

  const canDeleteDeployment = useCanDeleteDeployment();
  const menuItems = useMemo(() => {
    const items = [];

    // Hide "Update version" action in templates marketplace
    if (!isMarketplace) {
      items.push(
        <DropdownMenuItem
          key="update-version"
          onAction={() => showUpdateDrawer({ deployment: deployment, templateId: templateData.id })}
          isDisabled={!canUpdateTemplateDeployment.result}
          tooltip={canUpdateTemplateDeployment.message}
        >
          Update version
        </DropdownMenuItem>
      );
    }

    items.push(
      <DropdownMenuItem
        key="update-deployment"
        onAction={() =>
          navigate(
            `/template/${templateData.id}/version/${deployment.blueprintVersion.id}/deployments/${deployment.id}/update`,
            { state: { returnUrl: location.pathname } }
          )
        }
        isDisabled={!canUpdateTemplateDeploymentInputs.result}
        tooltip={canUpdateTemplateDeploymentInputs.message}
      >
        Update deployment
      </DropdownMenuItem>
    );
    const deleteAccess = canDeleteDeployment(deployment, templateData.space.id);

    items.push(
      <DropdownMenuItem
        key="delete"
        onAction={() =>
          showDeleteConfirmation({
            name: deployment.name,
            id: deployment.id,
            blueprintID: templateData.id,
          })
        }
        danger
        isDisabled={!deleteAccess.result}
        tooltip={deleteAccess.message}
      >
        Delete
      </DropdownMenuItem>
    );

    return items;
  }, [
    canUpdateTemplateDeployment,
    canUpdateTemplateDeploymentInputs,
    deployment,
    templateData,
    isMarketplace,
    canDeleteDeployment,
    navigate,
    location.pathname,
  ]);

  return (
    <TableSimpleCell {...cellProps}>
      {menuItems.length > 0 && (
        <DropdownMenuEllipsis tooltip="Deployment actions">{menuItems}</DropdownMenuEllipsis>
      )}
    </TableSimpleCell>
  );
};

export default TemplateDeploymentsRowActionsCell;
